import React, { useState, useEffect, useCallback, useRef } from 'react';
import DOMPurify from 'dompurify';
import { useTranslation } from './i18n/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dictionary = ({ API, isDarkMode, showNotification, userToken, hasPaidPlan, setCurrentView, setInitialProfileTab }) => {
  const { t } = useTranslation();

  // App-shell caches: read from localStorage synchronously so the page renders
  // chrome (config-driven titles, browse categories, category order, recent
  // searches, vocab count) from frame 1 on Ctrl+R instead of showing a skeleton
  // then popping into the real layout. Background fetches still run and
  // silently update the cache for next refresh.
  const _readCache = (key) => {
    try { const c = localStorage.getItem(key); if (c) return JSON.parse(c); } catch {}
    return null;
  };
  const _cachedDictConfig = _readCache('dictConfigCache');
  const _cachedCategories = _readCache('dictCategoriesCache');
  const _cachedOrderedCats = _readCache('dictOrderedCategoryNamesCache');
  const _cachedSearchHistory = _readCache('dictSearchHistoryCache');
  const _cachedTotalVocab = _readCache('dictTotalVocabCache');
  const _cachedQuickSuggestions = _readCache('dictQuickSuggestionsCache');
  const _cachedBrowseWordsMap = _readCache('dictBrowseWordsCache') || {};

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchHistory, setSearchHistory] = useState(_cachedSearchHistory || []);
  const [hasSearched, setHasSearched] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(!!_cachedDictConfig);
  const [dictConfig, setDictConfig] = useState(_cachedDictConfig || { is_visible: true, is_pro_only: false, hide_empty_categories: true, quick_suggestions_mode: 'custom', quick_suggestions_words: ['hello', 'goodbye', 'water', 'food', 'love', 'yes', 'no', 'please'], quick_suggestions_count: 8, search_min_length: 2, search_history_max: 8, search_history_display: 6, how_to_use_text: '', top_announcement: '' });
  const [showProModal, setShowProModal] = useState(false);
  const [quickSuggestions, setQuickSuggestions] = useState(_cachedQuickSuggestions || []);

  // Browse state — browseWords initialized from per-category cache for the
  // default activeCategory ('All') so the grid is populated from frame 1.
  const [browseWords, setBrowseWords] = useState(_cachedBrowseWordsMap['All'] || []);
  const [categories, setCategories] = useState(_cachedCategories || {});
  const [activeCategory, setActiveCategory] = useState('All');
  const [totalVocab, setTotalVocab] = useState(_cachedTotalVocab || 0);
  const [browseLoading, setBrowseLoading] = useState(false);
  const categoryBarRef = useRef(null);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [orderedCategoryNames, setOrderedCategoryNames] = useState(_cachedOrderedCats || []);

  // Update arrow visibility based on scroll position
  const updateScrollArrows = useCallback(() => {
    const el = categoryBarRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 5);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 5);
  }, []);

  // Check arrows on mount and when categories change
  useEffect(() => {
    updateScrollArrows();
    // Re-check after a small delay (fonts/layout may shift)
    const t = setTimeout(updateScrollArrows, 300);
    return () => clearTimeout(t);
  }, [categories, updateScrollArrows]);

  // Back to top scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const apiBase = API || API_URL;

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch(`${apiBase}/dictionary/settings`);
        if (res.ok) {
          const d = await res.json();
          setDictConfig(prev => {
            const merged = { ...prev, ...d };
            try { localStorage.setItem('dictConfigCache', JSON.stringify(merged)); } catch {}
            return merged;
          });
          // Load quick suggestions based on mode
          if (d.quick_suggestions_mode === 'random') {
            try {
              const randRes = await fetch(`${apiBase}/conversation-challenge/vocabulary/random?count=${d.quick_suggestions_count || 8}`);
              if (randRes.ok) {
                const randData = await randRes.json();
                const sugs = randData.words || [];
                setQuickSuggestions(sugs);
                try { localStorage.setItem('dictQuickSuggestionsCache', JSON.stringify(sugs)); } catch {}
              }
            } catch (e) { /* silent */ }
          } else {
            const sugs = d.quick_suggestions_words || ['hello', 'goodbye', 'water', 'food', 'love', 'yes', 'no', 'please'];
            setQuickSuggestions(sugs);
            try { localStorage.setItem('dictQuickSuggestionsCache', JSON.stringify(sugs)); } catch {}
          }
        }
      } catch (e) { /* silent */ }
      setConfigLoaded(true);
    };
    loadConfig();
  }, [apiBase]);

  // Load vocabulary categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const res = await fetch(`${apiBase}/conversation-challenge/vocabulary/categories`);
        if (res.ok) {
          const data = await res.json();
          const cats = data.categories || {};
          const total = data.total || 0;
          setCategories(cats);
          setTotalVocab(total);
          try { localStorage.setItem('dictCategoriesCache', JSON.stringify(cats)); } catch {}
          try { localStorage.setItem('dictTotalVocabCache', JSON.stringify(total)); } catch {}
        }
      } catch (e) { /* silent */ }
    };
    if (configLoaded) loadCategories();
  }, [apiBase, configLoaded]);

  // Load ordered category list from admin settings
  useEffect(() => {
    const loadCategoryOrder = async () => {
      try {
        const res = await fetch(`${apiBase}/conversation-challenge/vocabulary/word-categories`);
        if (res.ok) {
          const data = await res.json();
          const names = (data.word_categories || []).map(c => c.name);
          setOrderedCategoryNames(names);
          try { localStorage.setItem('dictOrderedCategoryNamesCache', JSON.stringify(names)); } catch {}
        }
      } catch (e) { /* silent */ }
    };
    if (configLoaded) loadCategoryOrder();
  }, [apiBase, configLoaded]);

  // Load browse words when category changes
  useEffect(() => {
    const loadBrowseWords = async () => {
      setBrowseLoading(true);
      try {
        const catParam = activeCategory === 'All' ? '' : `category=${encodeURIComponent(activeCategory)}&`;
        const headers = {};
        if (userToken) headers['Authorization'] = `Bearer ${userToken}`;
        const res = await fetch(`${apiBase}/conversation-challenge/vocabulary/browse?${catParam}per_page=100`, { headers });
        if (res.ok) {
          const data = await res.json();
          const words = data.words || [];
          setBrowseWords(words);
          // Update per-category cache so next refresh on this category renders the grid from frame 1
          try {
            const map = JSON.parse(localStorage.getItem('dictBrowseWordsCache') || '{}');
            map[activeCategory] = words;
            localStorage.setItem('dictBrowseWordsCache', JSON.stringify(map));
          } catch {}
        }
      } catch (e) { /* silent */ }
      setBrowseLoading(false);
    };
    if (configLoaded && totalVocab > 0) loadBrowseWords();
  }, [apiBase, activeCategory, configLoaded, totalVocab, userToken]);

  const selectCategory = (cat) => {
    setActiveCategory(cat);
    if (categoryBarRef.current) {
      const btn = categoryBarRef.current.querySelector(`[data-cat="${cat}"]`);
      if (btn) btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  };

  const handleBrowseCardClick = (word) => {
    setSearchQuery(word);
    searchDictionary(word);
  };

  // Search dictionary
  const searchDictionary = useCallback(async (word) => {
    const minLen = dictConfig.search_min_length || 2;
    if (!word || word.trim().length < minLen) {
      showNotification && showNotification(`${t('dict_min_chars')} ${minLen} ${t('dict_chars_suffix')}`, 'warning');
      return;
    }

    setIsSearching(true);
    setSearchResult(null);
    setHasSearched(true);

    try {
      const headers = {};
      if (userToken) headers['Authorization'] = `Bearer ${userToken}`;
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/search/${encodeURIComponent(word.trim())}`, { headers });
      if (response.ok) {
        const data = await response.json();
        if (!data.error) {
          const result = {
            searchedWord: word,
            word: word,
            darija: data.data?.darija || data.word || word,
            english: data.data?.english || (Array.isArray(data.data?.meanings) ? data.data.meanings.join(', ') : data.data?.meaning) || '',
            arabic: data.data?.arabic || '',
            phonetic: data.data?.pronunciation || data.data?.phonetic || '',
            wordType: data.data?.type || data.data?.wordType || '',
            category: data.data?.category || '',
            meaning: data.data?.meaning || (Array.isArray(data.data?.meanings) ? data.data.meanings.join(', ') : '') || '',
            grammarNote: data.data?.grammar_note || data.data?.grammarNote || '',
            genderNote: data.data?.gender_note || data.data?.genderNote || '',
            culturalNote: data.data?.cultural_note || data.data?.culturalNote || '',
            examples: data.data?.examples || [],
            relatedWords: data.data?.related_words || data.data?.relatedWords || [],
            audioUrl: data.data?.audioUrl || data.data?.audio_url || '',
            source: data.source || 'unknown'
          };
          setSearchResult(result);
          
          setSearchHistory(prev => {
            const newHistory = [word, ...prev.filter(w => w.toLowerCase() !== word.toLowerCase())].slice(0, dictConfig.search_history_max || 8);
            try { localStorage.setItem('dictSearchHistoryCache', JSON.stringify(newHistory)); } catch {}
            return newHistory;
          });
        } else {
          const safeErr = /(?:groq|gemini|deepseek|api key|ai is disabled|provider)/i.test(data.error || '')
            ? t('dict_word_not_found')
            : (data.error || t('dict_word_not_found'));
          showNotification && showNotification(safeErr, 'error');
        }
      } else {
        showNotification && showNotification(t('dict_search_failed'), 'error');
      }
    } catch (error) {
      console.error('Dictionary search error:', error);
      showNotification && showNotification('Search failed. Please try again.', 'error');
    } finally {
      setIsSearching(false);
    }
  }, [showNotification, dictConfig.search_min_length, dictConfig.search_history_max, userToken]);

  const handleSearch = (e) => {
    e.preventDefault();
    searchDictionary(searchQuery);
  };

  const handleHistoryClick = (word) => {
    setSearchQuery(word);
    searchDictionary(word);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResult(null);
    setHasSearched(false);
  };

  const showProGlass = dictConfig.is_pro_only && !hasPaidPlan;

  useEffect(() => {
    if (configLoaded && showProGlass && activeCategory === 'All') {
      setActiveCategory(dictConfig.free_preview_category || 'Travel');
    }
  }, [configLoaded, showProGlass, activeCategory, dictConfig.free_preview_category]);

  if (!configLoaded) {
    /* Skeleton — title + search-box shapes so layout doesn't jump when config arrives */
    return (
      <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
        <div className="flex flex-col items-center pt-8 md:pt-12 animate-pulse">
          <div className={`h-10 md:h-14 w-56 md:w-72 rounded-lg mb-3 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
          <div className={`h-4 md:h-5 w-48 md:w-64 rounded mb-8 ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`} />
          <div className={`w-full max-w-xl md:max-w-2xl px-4 mb-6`}>
            <div className={`h-12 md:h-14 rounded-full ${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 relative ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      
      {/* Top Announcement Banner */}
      {dictConfig.top_announcement && (
        <div className={`px-4 py-3 text-center text-sm ${isDarkMode ? 'bg-cyan-900/40 text-cyan-200 border-b border-cyan-800' : 'bg-cyan-50 text-cyan-800 border-b border-cyan-200'}`}>
          {dictConfig.top_announcement}
        </div>
      )}

      {/* Main Container - Centered like Google */}
      <div className={`flex flex-col items-center transition-all duration-500 ${hasSearched && searchResult ? 'pt-6 md:pt-8' : 'pt-8 md:pt-12'}`}>

        {/* Logo/Title Section */}
        <div className={`text-center mb-6 md:mb-8 transition-all duration-500 ${hasSearched && searchResult ? 'scale-90' : 'scale-100'}`}>
          <h1 className={`font-bold tracking-tight mb-2 transition-all duration-500 ${
            hasSearched && searchResult
              ? 'text-2xl md:text-3xl'
              : 'text-4xl md:text-6xl'
          } ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
            {t('dictionary_title')}
          </h1>
          <p className={`transition-all duration-500 ${
            hasSearched && searchResult ? 'text-xs md:text-sm' : 'text-sm md:text-lg'
          } ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {t('dictionary_subtitle')}
          </p>
        </div>

        {/* Search Box - Google Style */}
        <form onSubmit={handleSearch} className="w-full max-w-xl md:max-w-2xl px-4 mb-6">
          <div className={`relative flex items-center rounded-full border transition-all duration-200 hover:shadow-lg ${
            isDarkMode 
              ? 'bg-gray-800 border-gray-700 hover:border-gray-600 focus-within:border-blue-500 focus-within:shadow-blue-500/20' 
              : 'bg-white border-gray-200 hover:border-gray-300 focus-within:border-blue-400 focus-within:shadow-lg'
          }`}>
            {/* Search Icon */}
            <div className={`pl-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            
            {/* Input */}
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search_for_word')}
              className={`flex-1 px-4 py-3 md:py-4 text-base md:text-lg bg-transparent outline-none ${
                isDarkMode ? 'text-white placeholder-gray-500' : 'text-gray-800 placeholder-gray-400'
              }`}
            />
            
            {/* Clear Button */}
            {searchQuery && (
              <button
                type="button"
                onClick={clearSearch}
                className={`p-2 mr-1 rounded-full transition-colors ${
                  isDarkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            
            {/* Divider */}
            {searchQuery && (
              <div className={`h-6 w-px ${isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
            )}
            
            {/* Search Button */}
            <button
              type="submit"
              disabled={isSearching || !searchQuery.trim()}
              className={`p-2 mr-2 rounded-full transition-all ${
                isSearching || !searchQuery.trim()
                  ? isDarkMode ? 'text-gray-600' : 'text-gray-300'
                  : isDarkMode 
                    ? 'text-blue-400 hover:bg-blue-500/20' 
                    : 'text-blue-500 hover:bg-blue-50'
              }`}
            >
              {isSearching ? (
                <div className="w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {/* Quick Search Suggestions - Only show before search */}
        {!hasSearched && searchHistory.length === 0 && quickSuggestions.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 px-4 mb-8">
            {quickSuggestions.map((word) => (
              <button
                key={word}
                onClick={() => handleHistoryClick(word)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                {word}
              </button>
            ))}
          </div>
        )}

        {/* Search History */}
        {!hasSearched && searchHistory.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 px-4 mb-8">
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} mr-2 self-center`}>{t('dict_recent')}</span>
            {searchHistory.slice(0, dictConfig.search_history_display || 6).map((word, i) => (
              <button
                key={i}
                onClick={() => handleHistoryClick(word)}
                className={`px-3 py-1.5 rounded-full text-sm transition-all flex items-center gap-1 ${
                  isDarkMode 
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {word}
              </button>
            ))}
          </div>
        )}

        {/* Browse Section — visible when no search result */}
        {!searchResult && totalVocab > 0 && (
          <div className="w-full max-w-4xl px-4 mb-8">
            {/* Section Header */}
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className={`h-px flex-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
              <p className={`text-sm font-medium whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('dict_browse_verified')} ({totalVocab})
              </p>
              <div className={`h-px flex-1 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>
            </div>

            {/* Category Tabs */}
            <div className="relative mb-4">
              {/* Left fade + arrow (mobile only) */}
              {canScrollLeft && (
                <>
                  <div className={`absolute left-0 top-0 bottom-0 w-8 z-10 pointer-events-none md:hidden ${isDarkMode ? 'bg-gradient-to-r from-gray-900' : 'bg-gradient-to-r from-white'}`}></div>
                  <button
                    onClick={() => categoryBarRef.current?.scrollBy({ left: -150, behavior: 'smooth' })}
                    className={`absolute left-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full flex items-center justify-center shadow-md md:hidden ${
                      isDarkMode ? 'bg-gray-700 text-white border border-gray-600' : 'bg-white text-gray-700 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                  </button>
                </>
              )}
              {/* Right fade + arrow (mobile only) */}
              {canScrollRight && (
                <>
                  <div className={`absolute right-0 top-0 bottom-0 w-8 z-10 pointer-events-none md:hidden ${isDarkMode ? 'bg-gradient-to-l from-gray-900' : 'bg-gradient-to-l from-white'}`}></div>
                  <button
                    onClick={() => categoryBarRef.current?.scrollBy({ left: 150, behavior: 'smooth' })}
                    className={`absolute right-0 top-1/2 -translate-y-1/2 z-20 w-7 h-7 rounded-full flex items-center justify-center shadow-md md:hidden ${
                      isDarkMode ? 'bg-gray-700 text-white border border-gray-600' : 'bg-white text-gray-700 border border-gray-200'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </button>
                </>
              )}
              <div
                ref={categoryBarRef}
                className={`flex gap-1.5 md:gap-2 overflow-x-auto md:overflow-x-visible md:flex-wrap pb-2 ${canScrollLeft ? 'pl-8' : ''} ${canScrollRight ? 'pr-8' : ''} md:pl-0 md:pr-0`}
                style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                onScroll={updateScrollArrows}
              >
              <style>{`div[class*="overflow-x-auto"]::-webkit-scrollbar { display: none; }`}</style>
              {/* All tab — hidden for free users behind Pro glass (server returns only the preview category, so an "All" count would be misleading) */}
              {!showProGlass && (
                <button
                  data-cat="All"
                  onClick={() => selectCategory('All')}
                  className={`flex-shrink-0 md:flex-shrink px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all ${
                    activeCategory === 'All'
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                      : isDarkMode
                        ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  All ({totalVocab})
                </button>
              )}
              {(() => {
                // Build full category list: admin-ordered categories + any extra from word data
                const catCounts = categories || {};
                const allCatNames = [...new Set([
                  ...orderedCategoryNames,
                  ...Object.keys(catCounts).filter(c => c)
                ])];
                // Sort: use admin order first, then alphabetical for any extras, "Other" always last
                const sorted = allCatNames
                  .filter(c => c !== 'Other')
                  .sort((a, b) => {
                    const idxA = orderedCategoryNames.indexOf(a);
                    const idxB = orderedCategoryNames.indexOf(b);
                    if (idxA >= 0 && idxB >= 0) return idxA - idxB;
                    if (idxA >= 0) return -1;
                    if (idxB >= 0) return 1;
                    return a.localeCompare(b);
                  });
                if (allCatNames.includes('Other') || orderedCategoryNames.includes('Other')) sorted.push('Other');
                // Admin toggle: hide empty categories or show all
                const filtered = dictConfig.hide_empty_categories !== false
                  ? sorted.filter(cat => (catCounts[cat] || 0) > 0)
                  : sorted;
                return filtered.map(cat => (
                  <button
                    key={cat}
                    data-cat={cat}
                    onClick={() => selectCategory(cat)}
                    className={`flex-shrink-0 md:flex-shrink px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-semibold transition-all ${
                      activeCategory === cat
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-md'
                        : (catCounts[cat] || 0) === 0
                          ? isDarkMode
                            ? 'bg-gray-800/50 text-gray-500 border border-gray-700/50'
                            : 'bg-gray-50 text-gray-400 border border-gray-200/50'
                          : isDarkMode
                            ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                  >
                    {cat} ({catCounts[cat] || 0})
                  </button>
                ));
              })()}
              </div>
            </div>

            {/* Word Cards Grid — show spinner only if no cached words yet.
                If we have words (cached or just-fetched), keep them visible
                while a background refresh runs so the grid never disappears. */}
            {browseLoading && browseWords.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : browseWords.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-3">
                {browseWords.map((word, idx) => (
                  <button
                    key={`${word.english}-${idx}`}
                    onClick={() => handleBrowseCardClick(word.english || word.darija)}
                    className={`text-left p-2.5 md:p-4 rounded-xl border transition-all hover:-translate-y-0.5 cursor-pointer ${
                      isDarkMode
                        ? 'bg-gray-800 border-gray-700 hover:border-gray-600 hover:shadow-lg hover:shadow-gray-900/50'
                        : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
                    }`}
                  >
                    {/* English */}
                    <p className={`text-sm md:text-base font-semibold capitalize ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {word.english || '-'}
                    </p>
                    {/* Darija */}
                    <p className={`text-sm md:text-base mt-0.5 ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                      {word.darija || '-'}
                    </p>
                    {/* Arabic */}
                    {word.arabic && (
                      <p className={`text-sm mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} dir="rtl">
                        {word.arabic}
                      </p>
                    )}
                    {/* Badges */}
                    <div className="flex items-center gap-1.5 mt-2">
                      {word.type && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                          isDarkMode ? 'bg-purple-900/40 text-purple-300' : 'bg-purple-100 text-purple-700'
                        }`}>
                          {word.type}
                        </span>
                      )}
                      {word.category && (
                        <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                          isDarkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {word.category}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className={`text-center text-sm py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                {t('dict_no_words_category')}
              </p>
            )}
          </div>
        )}

        {/* Search Result Cards */}
        {searchResult && (
          <div className="w-full max-w-3xl px-4 animate-fadeIn">
            {/* Result Header */}
            <div className={`mb-5 pb-3 border-b flex items-center flex-wrap gap-2 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {t('dict_showing_result_for')} "<span className="font-medium">{searchResult.searchedWord}</span>"
              </p>
              {(searchResult.source === 'curated' || searchResult.source === 'cache' || searchResult.source === 'your_vocabulary') && (
                <span className="px-2 py-0.5 rounded text-xs bg-green-100 text-green-700 font-medium">
                  {t('dict_verified_check')}
                </span>
              )}
              {searchResult.wordType && (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  isDarkMode ? 'bg-purple-600/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                }`}>
                  {searchResult.wordType}
                </span>
              )}
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">

              {/* Row 1: English + Darija */}
              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-green-900/20 border-green-800/40' : 'bg-green-50 border-green-200'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  {t('dict_h_english')}
                </h3>
                <p className={`text-lg font-medium ${isDarkMode ? 'text-green-300' : 'text-green-700'}`}>
                  {searchResult.english || searchResult.meaning || '-'}
                </p>
              </div>

              <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-800/40' : 'bg-blue-50 border-blue-200'}`}>
                <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  {t('dict_h_darija_latin')}
                </h3>
                <p className={`text-lg font-medium ${isDarkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                  {searchResult.darija}
                </p>
              </div>

              {/* Row 2: Arabic + Pronunciation */}
              {searchResult.arabic && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-purple-900/20 border-purple-800/40' : 'bg-purple-50 border-purple-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {t('dict_h_arabic_script')}
                  </h3>
                  <p className={`text-xl font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`} dir="rtl">
                    {searchResult.arabic}
                  </p>
                </div>
              )}

              {searchResult.phonetic && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-cyan-900/20 border-cyan-800/40' : 'bg-cyan-50 border-cyan-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-cyan-400' : 'text-cyan-600'}`}>
                    {t('dict_h_pronunciation')}
                  </h3>
                  <p className={`text-lg font-medium italic ${isDarkMode ? 'text-cyan-300' : 'text-cyan-700'}`}>
                    {searchResult.phonetic}
                  </p>
                </div>
              )}

              {/* Row 3: Meaning/Definition (full width, only if different from English) */}
              {searchResult.meaning && searchResult.meaning !== searchResult.english && (
                <div className={`col-span-1 md:col-span-2 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-700/30 border-gray-600/40' : 'bg-gray-50 border-gray-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {t('dict_h_meaning')}
                  </h3>
                  <p className={`${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    {searchResult.meaning}
                  </p>
                </div>
              )}

              {/* Row 4: Word Type + Category */}
              {searchResult.wordType && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-purple-900/20 border-purple-800/40' : 'bg-purple-50 border-purple-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                    {t('dict_h_word_type')}
                  </h3>
                  <p className={`text-lg font-medium capitalize ${isDarkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                    {searchResult.wordType}
                  </p>
                </div>
              )}

              {searchResult.category && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-indigo-900/20 border-indigo-800/40' : 'bg-indigo-50 border-indigo-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {t('dict_h_category')}
                  </h3>
                  <p className={`text-lg font-medium ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>
                    {searchResult.category}
                  </p>
                </div>
              )}

              {/* Row 5: Gender Note + Audio */}
              {searchResult.genderNote && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-pink-900/20 border-pink-800/40' : 'bg-pink-50 border-pink-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`}>
                    {t('dict_h_gender_note')}
                  </h3>
                  <p className={`${isDarkMode ? 'text-pink-200' : 'text-pink-800'}`}>
                    {searchResult.genderNote}
                  </p>
                </div>
              )}

              {searchResult.audioUrl && (
                <div className={`p-4 rounded-xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-800/40' : 'bg-blue-50 border-blue-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {t('dict_h_audio')}
                  </h3>
                  <button
                    onClick={() => {
                      const audio = new Audio(searchResult.audioUrl);
                      audio.play().catch(e => console.error('Audio error:', e));
                    }}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-500 text-white'
                        : 'bg-blue-500 hover:bg-blue-600 text-white'
                    } shadow hover:scale-105`}
                    title="Play pronunciation"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                    </svg>
                    <span className="text-sm font-medium">{t('dict_play_pronunciation_text')}</span>
                  </button>
                </div>
              )}

              {/* Row 6: Grammar Note (full width, renders HTML) */}
              {searchResult.grammarNote && (
                <div className={`col-span-1 md:col-span-2 p-4 rounded-xl border ${isDarkMode ? 'bg-yellow-900/20 border-yellow-800/40' : 'bg-yellow-50 border-yellow-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                    {t('dict_h_grammar_note')}
                  </h3>
                  <div className={`${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`} style={{lineHeight: '1.6'}} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(searchResult.grammarNote)}} />
                </div>
              )}

              {/* Row 7: Examples (full width, renders HTML or array) */}
              {searchResult.examples && (Array.isArray(searchResult.examples) ? searchResult.examples.length > 0 : searchResult.examples) && (
                <div className={`col-span-1 md:col-span-2 p-4 rounded-xl border ${isDarkMode ? 'bg-blue-900/20 border-blue-800/40' : 'bg-blue-50 border-blue-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-2 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                    {t('dict_h_examples')}
                  </h3>
                  {typeof searchResult.examples === 'string' ? (
                    <div className={`${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`} style={{lineHeight: '1.6'}} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(searchResult.examples)}} />
                  ) : (
                    <ul className="space-y-2">
                      {(Array.isArray(searchResult.examples) ? searchResult.examples : [searchResult.examples]).map((ex, i) => (
                        <li key={i} className={`flex items-start gap-2 ${isDarkMode ? 'text-blue-200' : 'text-blue-800'}`}>
                          <span className="text-blue-400 mt-0.5">•</span>
                          <span>{typeof ex === 'string' ? ex : `${ex.darija} - ${ex.english}`}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Row 8: Cultural Note (full width, renders HTML) */}
              {searchResult.culturalNote && (
                <div className={`col-span-1 md:col-span-2 p-4 rounded-xl border ${isDarkMode ? 'bg-orange-900/20 border-orange-800/40' : 'bg-orange-50 border-orange-200'}`}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    Cultural Note
                  </h3>
                  <div className={`${isDarkMode ? 'text-orange-200' : 'text-orange-800'}`} style={{lineHeight: '1.6'}} dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(searchResult.culturalNote)}} />
                </div>
              )}
            </div>

            {/* Search Again Button */}
            <div className="text-center pb-8">
              <button
                onClick={clearSearch}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  isDarkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700 border border-gray-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {t('dict_search_another')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Back to Top Button — left side */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`fixed left-2 bottom-20 md:left-4 md:bottom-6 z-20 p-1.5 md:p-3 rounded-full shadow-lg transition-all hover:scale-110 ${
            isDarkMode
              ? 'bg-gray-700 text-white hover:bg-gray-600 border border-gray-600'
              : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
          }`}
          title={t('dict_back_to_top')}
        >
          <svg className="w-3.5 h-3.5 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
          </svg>
        </button>
      )}

<style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>

      {/* Pro paywall glass overlay — renders on top of the full Dictionary
          content for free users. Content is visible through the glass; the
          centered card holds title/message/Upgrade button. */}
      {showProGlass && (
        <>
          <div className="absolute inset-0 z-20 cursor-not-allowed"
               style={{ backdropFilter: 'blur(0.6px)', WebkitBackdropFilter: 'blur(0.6px)',
                        backgroundColor: isDarkMode ? 'rgba(17,24,39,0.08)' : 'rgba(255,255,255,0.06)' }}>
          </div>
          <div className="fixed z-30 pointer-events-none px-4" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(90vw, 28rem)' }}>
            <div className={`pointer-events-auto text-center p-8 max-w-md rounded-3xl shadow-2xl ring-4 ring-white/30 ${isDarkMode ? 'bg-gray-900/95' : 'bg-white/95'}`}>
              <span className="text-6xl mb-4 block">📖</span>
              <h2 className={`text-2xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('dict_pro_modal_title')}</h2>
              <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {t('dict_pro_modal_message')}
              </p>
              <button
                onClick={() => { if (setInitialProfileTab) setInitialProfileTab('membership'); if (setCurrentView) setCurrentView('profile'); }}
                className="px-6 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold shadow-2xl hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2 mx-auto cursor-pointer"
              >
                <span className="text-xl">🔓</span><span>{t('upgrade_to_pro')}</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Dictionary;
